import { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import type { Topology, Objects } from 'topojson-specification';
import type { GeoPermissibleObjects } from 'd3';

interface UsitcRecord {
  country: string;
  region: string;
  date: string;
  volume_kg: number;
  product_name: string;
  trade_direction: string;
}

interface UsitcWorldMapProps {
  data: UsitcRecord[];
  selectedCountries: Set<string>;
  selectedRegions: Set<string>;
  unit: 'kgs' | 'lbs';
}

const COUNTRY_ISO_MAP: Record<string, number | null> = {
  'China': 156,
  'Mexico': 484,
  'Canada': 124,
  'Germany': 276,
  'Japan': 392,
  'Brazil': 76,
  'South Korea': 410,
  'India': 356,
  'Netherlands': 528,
  'Belgium': 56,
  'France': 250,
  'Italy': 380,
  'United Kingdom': 826,
  'Spain': 724,
  'Australia': 36,
  'Taiwan': 158,
  'Saudi Arabia': 682,
  'United Arab Emirates': 784,
  'Turkey': 792,
  'Indonesia': 360,
  'Vietnam': 704,
  'Thailand': 764,
  'Malaysia': 458,
  'Singapore': 702,
  'Philippines': 608,
  'Colombia': 170,
  'Chile': 152,
  'Argentina': 32,
  'Peru': 604,
  'Ecuador': 218,
  'Guatemala': 320,
  'Honduras': 340,
  'Costa Rica': 188,
  'Dominican Republic': 214,
  'El Salvador': 222,
  'Panama': 591,
  'Trinidad and Tobago': 780,
  'Venezuela': 862,
  'Bolivia': 68,
  'Paraguay': 600,
  'Uruguay': 858,
  'Egypt': 818,
  'South Africa': 710,
  'Nigeria': 566,
  'Algeria': 12,
  'Morocco': 504,
  'Kenya': 404,
  'Poland': 616,
  'Czech Republic': 203,
  'Romania': 642,
  'Hungary': 348,
  'Greece': 300,
  'Portugal': 620,
  'Sweden': 752,
  'Norway': 578,
  'Denmark': 208,
  'Finland': 246,
  'Austria': 40,
  'Switzerland': 756,
  'Russia': 643,
  'Ukraine': 804,
  'Israel': 376,
  'Iran': 364,
  'Pakistan': 586,
  'Bangladesh': 50,
  'Sri Lanka': 144,
  'New Zealand': 554,
  'Hong Kong': 344,
  'United States': 840,
  'Ethiopia': 231,
  'Tanzania': 834,
  'Ghana': 288,
  'Angola': 24,
  'Mozambique': 508,
  'Cameroon': 120,
  'Ivory Coast': 384,
  'Niger': 562,
  'Zambia': 894,
  'Zimbabwe': 716,
  'Iraq': 368,
  'Kuwait': 414,
  'Qatar': 634,
  'Oman': 512,
  'Bahrain': 48,
  'Jordan': 400,
  'Lebanon': 422,
  'Syria': 760,
  'Libya': 434,
  'Tunisia': 788,
  'Sudan': 729,
  'Myanmar': 104,
  'Cambodia': 116,
  'Laos': 418,
  'Mongolia': 496,
  'Kazakhstan': 398,
  'Uzbekistan': 860,
  'Azerbaijan': 31,
  'Georgia': 268,
  'Armenia': 51,
  'Belarus': 112,
  'Slovakia': 703,
  'Bulgaria': 100,
  'Croatia': 191,
  'Serbia': 688,
  'Other': null,
};

type WorldTopology = Topology<Objects<{ [key: string]: unknown }>>;

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  country: string;
  volume: number;
}

export default function UsitcWorldMap({ data, selectedCountries, selectedRegions, unit }: UsitcWorldMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [topology, setTopology] = useState<WorldTopology | null>(null);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, country: '', volume: 0 });
  const [dimensions, setDimensions] = useState({ width: 800, height: 450 });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json')
      .then(r => r.json())
      .then((topo: WorldTopology) => {
        setTopology(topo);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0) {
          setDimensions({ width, height });
        }
      }
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!topology || !svgRef.current) return;

    const { width, height } = dimensions;
    const svg = d3.select(svgRef.current);

    const volumeByCountry = new Map<string, number>();
    for (const record of data) {
      const prev = volumeByCountry.get(record.country) ?? 0;
      volumeByCountry.set(record.country, prev + record.volume_kg);
    }

    const isoToVolume = new Map<number, number>();
    const isoToName = new Map<number, string>();
    for (const [country, volume] of volumeByCountry) {
      const iso = COUNTRY_ISO_MAP[country];
      if (iso != null) {
        isoToVolume.set(iso, (isoToVolume.get(iso) ?? 0) + volume);
        isoToName.set(iso, country);
      }
    }

    const maxVolume = Math.max(...Array.from(isoToVolume.values()), 1);
    const colorScale = d3.scaleSequential()
      .domain([0, maxVolume])
      .interpolator(d3.interpolateHsl('hsl(130, 63%, 8%)', '#39d353'));

    const countriesFeature = topojson.feature(topology, topology.objects.countries);

    const activeIsos = new Set<number>();
    if (selectedCountries.size > 0 || selectedRegions.size > 0) {
      if (selectedCountries.size > 0) {
        for (const name of selectedCountries) {
          const iso = COUNTRY_ISO_MAP[name];
          if (iso != null) activeIsos.add(iso);
        }
      }
      if (selectedRegions.size > 0) {
        for (const record of data) {
          if (selectedRegions.has(record.region)) {
            const iso = COUNTRY_ISO_MAP[record.country];
            if (iso != null) activeIsos.add(iso);
          }
        }
      }
    }

    const projection = d3.geoNaturalEarth1();

    if (activeIsos.size > 0 && 'features' in countriesFeature) {
      const matchingFeatures = countriesFeature.features.filter(f => {
        const id = typeof f.id === 'string' ? parseInt(f.id) : f.id as number;
        return activeIsos.has(id);
      });

      if (matchingFeatures.length > 0) {
        const collection = { type: 'FeatureCollection' as const, features: matchingFeatures };
        const padding = 40;
        projection.fitExtent(
          [[padding, padding], [width - padding, height - padding]],
          collection as GeoPermissibleObjects
        );
      } else {
        projection.fitSize([width, height], countriesFeature as GeoPermissibleObjects);
      }
    } else {
      projection.fitSize([width, height], countriesFeature as GeoPermissibleObjects);
    }

    const path = d3.geoPath().projection(projection);

    svg.selectAll('*').remove();

    const g = svg.append('g');

    if ('features' in countriesFeature) {
      g.selectAll<SVGPathElement, (typeof countriesFeature.features)[0]>('path.country')
        .data(countriesFeature.features)
        .join('path')
        .attr('class', 'country')
        .attr('d', d => path(d) ?? '')
        .attr('fill', d => {
          const id = typeof d.id === 'string' ? parseInt(d.id) : d.id as number;
          const volume = isoToVolume.get(id);
          return volume != null ? colorScale(volume) : '#2a2a2a';
        })
        .attr('stroke', '#1a1a1a')
        .attr('stroke-width', 0.4)
        .style('cursor', d => {
          const id = typeof d.id === 'string' ? parseInt(d.id) : d.id as number;
          return isoToVolume.has(id) ? 'pointer' : 'default';
        })
        .on('mousemove', (event, d) => {
          const id = typeof d.id === 'string' ? parseInt(d.id) : d.id as number;
          const name = isoToName.get(id) ?? '';
          const volume = isoToVolume.get(id) ?? 0;
          const rect = svgRef.current?.getBoundingClientRect();
          if (rect) {
            setTooltip({
              visible: true,
              x: event.clientX - rect.left + 10,
              y: event.clientY - rect.top - 30,
              country: name || String(id),
              volume,
            });
          }
        })
        .on('mouseleave', () => {
          setTooltip(prev => ({ ...prev, visible: false }));
        });
    }

    const meshFeature = topojson.mesh(topology, topology.objects.countries as topojson.GeometryCollection, (a, b) => a !== b);
    g.append('path')
      .datum(meshFeature)
      .attr('fill', 'none')
      .attr('stroke', '#111')
      .attr('stroke-width', 0.3)
      .attr('d', path);

    const legendWidth = 120;
    const legendHeight = 8;
    const legendX = width - legendWidth - 16;
    const legendY = height - 32;

    const defs = svg.append('defs');
    const gradId = 'map-legend-grad';
    const grad = defs.append('linearGradient').attr('id', gradId);
    grad.append('stop').attr('offset', '0%').attr('stop-color', 'hsl(130, 63%, 8%)');
    grad.append('stop').attr('offset', '100%').attr('stop-color', '#39d353');

    const legend = svg.append('g').attr('transform', `translate(${legendX},${legendY})`);
    legend.append('rect')
      .attr('width', legendWidth)
      .attr('height', legendHeight)
      .attr('rx', 2)
      .attr('fill', `url(#${gradId})`);

    legend.append('text')
      .attr('x', 0)
      .attr('y', legendHeight + 12)
      .attr('fill', 'rgba(255,255,255,0.5)')
      .attr('font-size', 10)
      .text('Low');

    legend.append('text')
      .attr('x', legendWidth)
      .attr('y', legendHeight + 12)
      .attr('fill', 'rgba(255,255,255,0.5)')
      .attr('font-size', 10)
      .attr('text-anchor', 'end')
      .text('High');

  }, [topology, data, selectedCountries, selectedRegions, dimensions]);

  const formatVolume = (vol: number) => {
    const value = unit === 'lbs' ? vol * 2.20462 : vol;
    return `${Math.round(value).toLocaleString()} ${unit}`;
  };

  return (
    <div ref={containerRef} className="relative w-full h-full">
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-rs-off-white/40 text-sm">Loading map…</span>
        </div>
      )}
      <svg
        ref={svgRef}
        className="w-full h-full"
        viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
        preserveAspectRatio="xMidYMid meet"
      />
      {tooltip.visible && (
        <div
          className="absolute pointer-events-none bg-rs-jet border border-rs-jet-light rounded px-2 py-1.5 text-xs text-rs-off-white z-10"
          style={{ left: tooltip.x, top: tooltip.y }}
        >
          <div className="font-medium">{tooltip.country}</div>
          <div>{tooltip.volume > 0 ? formatVolume(tooltip.volume) : 'No data'}</div>
        </div>
      )}
    </div>
  );
}
